import { createClient } from './client';

const BUCKET = 'videos';

/**
 * Uploads a video file to Supabase Storage and returns the public URL.
 * @param file - The video File object to upload
 * @param onProgress - Optional callback for upload progress (0-100)
 * @returns The public URL of the uploaded video, or throws on error
 */
export async function uploadVideoToStorage(
  file: File,
  onProgress?: (progress: number) => void
): Promise<string> {
  const supabase = createClient();

  // Generate a unique file path using timestamp + sanitized filename
  const timestamp = Date.now();
  const sanitizedName = file.name.replace(/[^a-zA-Z0-9._-]/g, '_');
  const filePath = `uploads/${timestamp}_${sanitizedName}`;

  // Simulate progress start
  onProgress?.(10);

  const { data, error } = await supabase.storage
    .from(BUCKET)
    .upload(filePath, file, {
      cacheControl: '3600',
      upsert: false,
    });

  if (error) {
    throw new Error(`Upload failed: ${error.message}`);
  }

  onProgress?.(90);

  // Get the public URL
  const { data: urlData } = supabase.storage
    .from(BUCKET)
    .getPublicUrl(data.path);

  onProgress?.(100);

  return urlData.publicUrl;
}

/**
 * Deletes a video file from Supabase Storage by its public URL.
 * @param publicUrl - The public URL of the video to delete
 */
export async function deleteVideoFromStorage(publicUrl: string): Promise<void> {
  const supabase = createClient();

  // Extract the file path from the public URL
  const url = new URL(publicUrl);
  const pathParts = url.pathname.split(`/storage/v1/object/public/${BUCKET}/`);
  if (pathParts.length < 2) return;

  const filePath = decodeURIComponent(pathParts[1]);

  const { error } = await supabase.storage.from(BUCKET).remove([filePath]);
  if (error) {
    console.error('Failed to delete video from storage:', error.message);
  }
}

/**
 * Uploads a thumbnail image to Supabase Storage and returns the public URL.
 * @param file - The image File object to upload
 * @returns The public URL of the uploaded thumbnail, or throws on error
 */
export async function uploadThumbnailToStorage(file: File): Promise<string> {
  const supabase = createClient();

  const timestamp = Date.now();
  const sanitizedName = file.name.replace(/[^a-zA-Z0-9._-]/g, '_');
  const filePath = `thumbnails/${timestamp}_${sanitizedName}`;

  const { data, error } = await supabase.storage
    .from(BUCKET)
    .upload(filePath, file, {
      cacheControl: '3600',
      upsert: false,
    });

  if (error) {
    throw new Error(`Thumbnail upload failed: ${error.message}`);
  }

  const { data: urlData } = supabase.storage
    .from(BUCKET)
    .getPublicUrl(data.path);

  return urlData.publicUrl;
}
